import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { Character, Profile } from '@/types/database';

type AuthStatus = 'initializing' | 'unauthenticated' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  character: Character | null;
  error: string | null;

  initialize: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshCharacter: () => Promise<void>;
  setCharacter: (character: Character) => void;
  signOut: () => Promise<void>;
  setError: (message: string | null) => void;
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle<Profile>();

  if (error) {
    // PGRST116 is "no rows" - profile may not exist yet right after signup
    // before the handle_new_user trigger commits. Treat as null silently.
    if (error.code !== 'PGRST116') {
      console.error('Failed to fetch profile', error);
    }
    return null;
  }
  return data;
}

async function fetchCharacter(userId: string): Promise<Character | null> {
  // v1 is single-character-per-account, but characters has no UNIQUE on
  // profile_id. Take the most recently created row defensively.
  const { data, error } = await supabase
    .from('characters')
    .select('*')
    .eq('profile_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle<Character>();

  if (error) {
    if (error.code !== 'PGRST116') {
      console.error('Failed to fetch character', error);
    }
    return null;
  }
  return data;
}

let initPromise: Promise<void> | null = null;
let listenerBound = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'initializing',
  user: null,
  session: null,
  profile: null,
  character: null,
  error: null,

  initialize: async () => {
    if (initPromise) return initPromise;

    initPromise = (async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        set({ status: 'unauthenticated', error: error.message });
      } else {
        const session = data.session;
        const user = session?.user ?? null;
        const [profile, character] = user
          ? await Promise.all([fetchProfile(user.id), fetchCharacter(user.id)])
          : [null, null];
        set({
          status: user ? 'authenticated' : 'unauthenticated',
          session,
          user,
          profile,
          character,
          error: null,
        });
      }

      if (!listenerBound) {
        listenerBound = true;
        supabase.auth.onAuthStateChange(async (_event, session) => {
          const user = session?.user ?? null;
          if (!user) {
            set({
              status: 'unauthenticated',
              session: null,
              user: null,
              profile: null,
              character: null,
            });
            return;
          }
          // Don't block the listener on the row fetches.
          set({ status: 'authenticated', session, user });
          const [profile, character] = await Promise.all([
            fetchProfile(user.id),
            fetchCharacter(user.id),
          ]);
          // Only commit if the user is still this user.
          if (get().user?.id === user.id) set({ profile, character });
        });
      }
    })();

    return initPromise;
  },

  refreshProfile: async () => {
    const user = get().user;
    if (!user) return;
    const profile = await fetchProfile(user.id);
    set({ profile });
  },

  refreshCharacter: async () => {
    const user = get().user;
    if (!user) return;
    const character = await fetchCharacter(user.id);
    set({ character });
  },

  setCharacter: (character) => set({ character }),

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      set({ error: error.message });
      return;
    }
    set({
      status: 'unauthenticated',
      user: null,
      session: null,
      profile: null,
      character: null,
      error: null,
    });
  },

  setError: (message) => set({ error: message }),
}));
